import { useCallback, useEffect, useState } from 'react'

import { unwrapPleadingCaptionsForDisplay } from '@/agent-v2/artifact-editor/custom_markdown_blocks/pleading-caption'
import { wrapTablesWithDiv } from '@/chat-common/util/source-html-editing-functions'
import { convertMarkdownToHtml } from '@/content-viewer/util/markdown-to-html'
import { fetchMatterArtifactMarkdown } from '@/matter-artifacts/fetch/get-matter-artifact-version-viewable-url'

type UseMatterArtifactViewableMarkdownOptions = {
  matterArtifactVersionId: string
}

export function useMatterArtifactViewableMarkdown({ matterArtifactVersionId }: UseMatterArtifactViewableMarkdownOptions) {
  const [markdownString, setMarkdownString] = useState<string | null>(null)
  const [htmlString, setHtmlString] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [refetchCount, setRefetchCount] = useState(0)

  const retry = useCallback(() => setRefetchCount((previous) => previous + 1), [])

  useEffect(() => {
    let cancelled = false

    async function loadMarkdown() {
      setIsLoading(true)
      setHasError(false)
      setMarkdownString(null)
      setHtmlString(null)

      try {
        const markdown = await fetchMatterArtifactMarkdown(matterArtifactVersionId)
        const html = await convertMarkdownToHtml(unwrapPleadingCaptionsForDisplay(markdown))
        if (!cancelled) {
          setMarkdownString(markdown)
          setHtmlString(wrapTablesWithDiv(html))
        }
      } catch {
        if (!cancelled) setHasError(true)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    loadMarkdown()
    return () => {
      cancelled = true
    }
  }, [matterArtifactVersionId, refetchCount])

  return { markdownString, htmlString, isLoading, hasError, retry }
}
